import * as React from 'react';
import {
  login as authLogin,
  logout as authLogout,
  silentRefresh,
  exchangeCodeForTokens,
  getAccessToken,
  getTokenClaims,
  hasRefreshToken,
  checkSessionTimeout,
  updateLastActivity,
  type IdTokenClaims,
} from '../services/auth-client';

/**
 * Authentication state management for the Admin Console.
 * Extends the base auth pattern with role extraction from EntraID token claims.
 * Supports System_Admin and Domain_Admin roles.
 * 30-minute session timeout enforced via activity tracking.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export type AdminRole = 'System_Admin' | 'Domain_Admin';

export interface AuthUser {
  readonly userId: string;
  readonly email: string;
  readonly name: string;
  readonly role: AdminRole;
}

// ─── State ───────────────────────────────────────────────────────────────────

export interface AuthState {
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  readonly user: AuthUser | null;
  readonly error: string | null;
}

export const initialAuthState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  error: null,
};

// ─── Actions ─────────────────────────────────────────────────────────────────

type AuthAction =
  | { readonly type: 'AUTH_LOADING' }
  | { readonly type: 'AUTH_SUCCESS'; readonly payload: AuthUser }
  | { readonly type: 'AUTH_FAILURE'; readonly payload: string }
  | { readonly type: 'AUTH_LOGOUT' }
  | { readonly type: 'AUTH_SESSION_EXPIRED' };

// ─── Reducer ─────────────────────────────────────────────────────────────────

export function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_LOADING':
      return { ...state, isLoading: true, error: null };
    case 'AUTH_SUCCESS':
      return { isAuthenticated: true, isLoading: false, user: action.payload, error: null };
    case 'AUTH_FAILURE':
      return { isAuthenticated: false, isLoading: false, user: null, error: action.payload };
    case 'AUTH_LOGOUT':
      return { isAuthenticated: false, isLoading: false, user: null, error: null };
    case 'AUTH_SESSION_EXPIRED':
      return { isAuthenticated: false, isLoading: false, user: null, error: 'Session expired. Please log in again.' };
    default:
      return state;
  }
}

// ─── Role Extraction ─────────────────────────────────────────────────────────

/**
 * Extracts the admin role from EntraID token claims.
 * Checks both App Roles (claims.roles) and group membership (claims.groups).
 * Returns null if user does not have an admin role.
 */
function extractAdminRole(claims: IdTokenClaims): AdminRole | null {
  // Check App Roles first
  const roles = claims.roles ?? [];
  if (roles.includes('System_Admin')) return 'System_Admin';
  if (roles.includes('Domain_Admin')) return 'Domain_Admin';

  // Check group membership (group Object IDs in the groups claim)
  // Only specific groups grant admin access
  const groups = claims.groups ?? [];
  const SYSTEM_ADMIN_GROUP = 'd128f213-e778-4faa-9605-c07456c64846';

  if (groups.includes(SYSTEM_ADMIN_GROUP)) {
    return 'System_Admin';
  }

  // No admin role found — deny access to admin console
  return null;
}

/**
 * Builds an AuthUser from decoded token claims.
 * Returns null if the user does not have an admin role.
 */
function buildAuthUser(claims: IdTokenClaims): AuthUser | null {
  const role = extractAdminRole(claims);
  if (!role) return null;

  return {
    userId: claims.oid ?? claims.sub,
    email: claims.preferred_username,
    name: claims.name,
    role,
  };
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface AuthContextValue {
  readonly state: AuthState;
  readonly login: () => Promise<void>;
  readonly logout: () => void;
  readonly handleCallback: (code: string) => Promise<void>;
  readonly getAccessToken: () => string | null;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

interface AuthProviderProps {
  readonly children: React.ReactNode;
}

/**
 * AuthProvider for the admin console. Manages EntraID OAuth2 state and
 * extracts admin role from token claims. Rejects non-admin users.
 */
export function AuthProvider({ children }: AuthProviderProps): React.JSX.Element {
  const [state, dispatch] = React.useReducer(authReducer, initialAuthState);
  const sessionCheckRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  // Attempt session restoration on mount
  React.useEffect(() => {
    async function restoreSession(): Promise<void> {
      if (!hasRefreshToken()) {
        dispatch({ type: 'AUTH_FAILURE', payload: '' });
        return;
      }

      const refreshed = await silentRefresh();
      if (refreshed) {
        const claims = getTokenClaims();
        if (claims) {
          const user = buildAuthUser(claims);
          if (user) {
            dispatch({ type: 'AUTH_SUCCESS', payload: user });
            return;
          }
        }
        // No admin role in token
        dispatch({ type: 'AUTH_FAILURE', payload: 'Access denied. Administrator role required.' });
      } else {
        dispatch({ type: 'AUTH_FAILURE', payload: '' });
      }
    }

    restoreSession();
  }, []);

  // Session timeout checker — runs every 60 seconds
  React.useEffect(() => {
    if (!state.isAuthenticated) {
      if (sessionCheckRef.current) {
        clearInterval(sessionCheckRef.current);
        sessionCheckRef.current = null;
      }
      return;
    }

    sessionCheckRef.current = setInterval(() => {
      if (checkSessionTimeout()) {
        dispatch({ type: 'AUTH_SESSION_EXPIRED' });
        void authLogin();
      }
    }, 60_000);

    return () => {
      if (sessionCheckRef.current) {
        clearInterval(sessionCheckRef.current);
      }
    };
  }, [state.isAuthenticated]);

  // Track user activity for session timeout
  React.useEffect(() => {
    if (!state.isAuthenticated) return;

    const handleActivity = (): void => {
      updateLastActivity();
    };

    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('keydown', handleActivity);

    return () => {
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('keydown', handleActivity);
    };
  }, [state.isAuthenticated]);

  const login = React.useCallback(async (): Promise<void> => {
    await authLogin();
  }, []);

  const logout = React.useCallback((): void => {
    dispatch({ type: 'AUTH_LOGOUT' });
    authLogout();
  }, []);

  const handleCallback = React.useCallback(async (code: string): Promise<void> => {
    dispatch({ type: 'AUTH_LOADING' });
    try {
      await exchangeCodeForTokens(code);
      const claims = getTokenClaims();
      if (!claims) {
        dispatch({ type: 'AUTH_FAILURE', payload: 'Failed to decode token.' });
        return;
      }
      const user = buildAuthUser(claims);
      if (!user) {
        dispatch({ type: 'AUTH_FAILURE', payload: 'Access denied. Administrator role required.' });
        return;
      }
      dispatch({ type: 'AUTH_SUCCESS', payload: user });
    } catch (err) {
      dispatch({
        type: 'AUTH_FAILURE',
        payload: err instanceof Error ? err.message : 'Authentication failed',
      });
    }
  }, []);

  const contextValue = React.useMemo(
    (): AuthContextValue => ({
      state,
      login,
      logout,
      handleCallback,
      getAccessToken,
    }),
    [state, login, logout, handleCallback],
  );

  return React.createElement(AuthContext.Provider, { value: contextValue }, children);
}

// ─── Consumer Hook ───────────────────────────────────────────────────────────

/**
 * Hook to access authentication state and actions.
 * Must be used within an AuthProvider.
 */
export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
